import { useState, useEffect } from 'react';
import { getUsers, getAziende, createUserAzienda, createUserAgente, deleteUser } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, User, Building2, Briefcase, Trash2, Loader2 } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const UtentiPage = () => {
  const [users, setUsers] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('azienda');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formAzienda, setFormAzienda] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: '',
    id_azienda: '',
  });

  const [formAgente, setFormAgente] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: '',
    percentuale_commissione: 20,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, aziendeRes] = await Promise.all([
        getUsers(),
        getAziende()
      ]);
      setUsers(usersRes.data);
      setAziende(aziendeRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAzienda = async (e) => {
    e.preventDefault();
    if (!formAzienda.id_azienda) {
      toast.error('Seleziona un\'azienda');
      return;
    }
    setSubmitting(true);
    try {
      await createUserAzienda(formAzienda);
      toast.success('Utente azienda creato');
      setDialogOpen(false);
      setFormAzienda({ email: '', password: '', nome: '', cognome: '', id_azienda: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAgente = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createUserAgente(formAgente);
      toast.success('Utente agente creato');
      setDialogOpen(false);
      setFormAgente({ email: '', password: '', nome: '', cognome: '', percentuale_commissione: 20 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (user) => {
    if (user.ruolo === 'admin') {
      toast.error('Non puoi eliminare un admin');
      return;
    }
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await deleteUser(userToDelete.id);
      toast.success('Utente eliminato');
      loadData();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const openDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = 
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.nome?.toLowerCase().includes(search.toLowerCase()) ||
      u.cognome?.toLowerCase().includes(search.toLowerCase());
    const matchRuolo = filterRuolo === 'all' || u.ruolo === filterRuolo;
    return matchSearch && matchRuolo;
  });

  const getRuoloBadge = (ruolo) => {
    switch (ruolo) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
      case 'agente':
        return <Badge className="bg-blue-100 text-blue-700">Agente</Badge>;
      case 'azienda':
        return <Badge className="bg-green-100 text-green-700">Azienda</Badge>;
      default:
        return <Badge variant="secondary">{ruolo}</Badge>;
    }
  };

  const getRuoloIcon = (ruolo) => {
    switch (ruolo) {
      case 'admin':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'agente':
        return <Briefcase className="h-4 w-4 text-blue-600" />;
      case 'azienda':
        return <Building2 className="h-4 w-4 text-green-600" />;
      default:
        return <User className="h-4 w-4 text-slate-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="utenti-page">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 lg:mb-8">
        <div>
          <h1 
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Gestione Utenti
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Crea e gestisci gli account utente</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => openDialog('azienda')}
            data-testid="new-user-azienda-btn"
            className="w-full sm:w-auto"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Utente Azienda
          </Button>
          <Button 
            className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" 
            onClick={() => openDialog('agente')}
            data-testid="new-user-agente-btn"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            Nuovo Agente
          </Button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per nome, cognome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-utenti"
          />
        </div>
        <Select value={filterRuolo} onValueChange={setFilterRuolo}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-ruolo">
            <SelectValue placeholder="Tutti i ruoli" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="agente">Agente</SelectItem>
            <SelectItem value="azienda">Azienda</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredUsers.map((user) => (
          <Card key={user.id} className={!user.attivo ? 'opacity-60' : ''} data-testid={`user-card-${user.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    {getRuoloIcon(user.ruolo)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{user.nome} {user.cognome}</p>
                    <p className="text-xs text-slate-500 font-mono">{user.id}</p>
                  </div>
                </div>
                {getRuoloBadge(user.ruolo)}
              </div>

              <div className="text-sm text-slate-600 mb-3 truncate">{user.email}</div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  {user.ruolo === 'azienda' && user.id_azienda && (
                    <span>Azienda: <span className="font-mono">{user.id_azienda}</span></span>
                  )}
                  {user.ruolo === 'agente' && user.percentuale_commissione && (
                    <span>Commissione: {user.percentuale_commissione}%</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {user.attivo ? (
                    <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                  ) : (
                    <Badge variant="secondary">Disattivo</Badge>
                  )}
                  {user.ruolo !== 'admin' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteDialog(user)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Nessun utente trovato
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="utenti-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Utente</th>
                  <th className="text-left p-4 font-medium text-slate-600">Email</th>
                  <th className="text-center p-4 font-medium text-slate-600">Ruolo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Dettagli</th>
                  <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                  <th className="text-right p-4 font-medium text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className={`border-b hover:bg-slate-50 transition-colors ${!user.attivo ? 'opacity-50' : ''}`}
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100">
                          {getRuoloIcon(user.ruolo)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.nome} {user.cognome}</p>
                          <p className="text-xs text-slate-500 font-mono">{user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{user.email}</td>
                    <td className="p-4 text-center">{getRuoloBadge(user.ruolo)}</td>
                    <td className="p-4">
                      {user.ruolo === 'azienda' && user.id_azienda && (
                        <span className="text-sm text-slate-600">
                          Azienda: <span className="font-mono">{user.id_azienda}</span>
                        </span>
                      )}
                      {user.ruolo === 'agente' && user.percentuale_commissione && (
                        <span className="text-sm text-slate-600">
                          Commissione: {user.percentuale_commissione}%
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {user.attivo ? (
                        <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                      ) : (
                        <Badge variant="secondary">Disattivo</Badge>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {user.ruolo !== 'admin' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(user)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              Nessun utente trovato
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {dialogType === 'azienda' ? 'Nuovo Utente Azienda' : 'Nuovo Agente'}
            </DialogTitle>
          </DialogHeader>

          {dialogType === 'azienda' ? (
            <form onSubmit={handleCreateAzienda} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome-azienda">Nome *</Label>
                  <Input
                    id="nome-azienda"
                    value={formAzienda.nome}
                    onChange={(e) => setFormAzienda({...formAzienda, nome: e.target.value})}
                    required
                    data-testid="input-nome-azienda"
                  />
                </div>
                <div>
                  <Label htmlFor="cognome-azienda">Cognome *</Label>
                  <Input
                    id="cognome-azienda"
                    value={formAzienda.cognome}
                    onChange={(e) => setFormAzienda({...formAzienda, cognome: e.target.value})}
                    required
                    data-testid="input-cognome-azienda"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email-azienda">Email *</Label>
                <Input
                  id="email-azienda"
                  type="email"
                  value={formAzienda.email}
                  onChange={(e) => setFormAzienda({...formAzienda, email: e.target.value})}
                  required
                  data-testid="input-email-azienda"
                />
              </div>
              <div>
                <Label htmlFor="password-azienda">Password *</Label>
                <Input
                  id="password-azienda"
                  type="password"
                  value={formAzienda.password}
                  onChange={(e) => setFormAzienda({...formAzienda, password: e.target.value})}
                  required
                  data-testid="input-password-azienda"
                />
              </div>
              <div>
                <Label htmlFor="id-azienda">Azienda associata *</Label>
                <Select 
                  value={formAzienda.id_azienda} 
                  onValueChange={(val) => setFormAzienda({...formAzienda, id_azienda: val})}
                >
                  <SelectTrigger data-testid="select-azienda">
                    <SelectValue placeholder="Seleziona azienda" />
                  </SelectTrigger>
                  <SelectContent>
                    {aziende.map((az) => (
                      <SelectItem key={az.id_azienda} value={az.id_azienda}>
                        {az.ragione_sociale} ({az.id_azienda})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" 
                  disabled={submitting}
                  data-testid="submit-user-azienda"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea Utente'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleCreateAgente} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome-agente">Nome *</Label>
                  <Input
                    id="nome-agente"
                    value={formAgente.nome}
                    onChange={(e) => setFormAgente({...formAgente, nome: e.target.value})}
                    required
                    data-testid="input-nome-agente"
                  />
                </div>
                <div>
                  <Label htmlFor="cognome-agente">Cognome *</Label>
                  <Input
                    id="cognome-agente"
                    value={formAgente.cognome}
                    onChange={(e) => setFormAgente({...formAgente, cognome: e.target.value})}
                    required
                    data-testid="input-cognome-agente"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email-agente">Email *</Label>
                <Input
                  id="email-agente"
                  type="email"
                  value={formAgente.email}
                  onChange={(e) => setFormAgente({...formAgente, email: e.target.value})}
                  required
                  data-testid="input-email-agente"
                />
              </div>
              <div>
                <Label htmlFor="password-agente">Password *</Label>
                <Input
                  id="password-agente"
                  type="password"
                  value={formAgente.password}
                  onChange={(e) => setFormAgente({...formAgente, password: e.target.value})}
                  required
                  data-testid="input-password-agente"
                />
              </div>
              <div>
                <Label htmlFor="commissione">Percentuale Commissione (%)</Label>
                <Input
                  id="commissione"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formAgente.percentuale_commissione}
                  onChange={(e) => setFormAgente({...formAgente, percentuale_commissione: parseFloat(e.target.value) || 0})}
                  data-testid="input-commissione"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" 
                  disabled={submitting}
                  data-testid="submit-user-agente"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea Agente'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Elimina Utente"
        description="Eliminando questo utente, non potrà più accedere al sistema. Questa azione è irreversibile."
        itemName={userToDelete ? `${userToDelete.nome || ''} ${userToDelete.cognome || ''} (${userToDelete.email})` : ''}
        loading={deleting}
      />
    </div>
  );
};

export default UtentiPage;
